import {
  makeFrame,
  type FacePackerPolygon,
  type SolarLatLng,
} from '@payaca/components/solarRoofView/SolarRoofView';
import { type SolarRoofPlane } from '@payaca/helpers/solarMounting';

/**
 * Pure rotation maths for the Roof step's "Rotate face" / "Straighten"
 * controls. All rotation happens in a local METRIC frame (via the face
 * packer's canonical `makeFrame`) — rotating raw lat/lng degrees would shear
 * the shape, because a degree of longitude is shorter than a degree of
 * latitude everywhere off the equator.
 *
 * Sign convention: positive degrees rotate CLOCKWISE in plan view, matching
 * compass azimuths (so a face rotated by +N has its azimuth increased by N).
 */

const degToRad = (deg: number) => (deg * Math.PI) / 180;
const radToDeg = (rad: number) => (rad * 180) / Math.PI;

/** Mean of the ring's vertices — the same centroid `packFace` frames around. */
export const ringCentroid = (ring: SolarLatLng[]): SolarLatLng => {
  let lat = 0;
  let lng = 0;
  for (const point of ring) {
    lat += point.lat;
    lng += point.lng;
  }
  return { lat: lat / ring.length, lng: lng / ring.length };
};

/** Wrap any angle into the [0, 360) compass range. */
export const normaliseAzimuthDegrees = (degrees: number): number =>
  ((degrees % 360) + 360) % 360;

/**
 * Build a rigid rotation around an explicit pivot, shared by roofs and panels.
 */
export const makePointRotation = (
  centre: SolarLatLng,
  deltaDegrees: number
): ((point: SolarLatLng) => SolarLatLng) => {
  const frame = makeFrame(centre);
  const rad = degToRad(deltaDegrees);
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  // Clockwise rotation in an east-x / north-y frame (CCW would be x cos − y sin).
  return (point: SolarLatLng): SolarLatLng => {
    if (deltaDegrees === 0) return point;
    const m = frame.toM(point);
    return frame.toLatLng({
      x: m.x * cos + m.y * sin,
      y: -m.x * sin + m.y * cos,
    });
  };
};

/** Turn flush modules in the physical roof plane to the desired plan bearing. */
export const makeFlushArrayRotation = (
  centre: SolarLatLng,
  roof: SolarRoofPlane,
  fromBearing: number,
  toBearing: number
): ((point: SolarLatLng) => SolarLatLng) => {
  const frame = makeFrame(centre);
  const bearing = degToRad(roof.azimuthDegrees);
  const downhill = { x: Math.sin(bearing), y: Math.cos(bearing) };
  const cross = { x: downhill.y, y: -downhill.x };
  const cosPitch = Math.cos(degToRad(roof.pitchDegrees));
  const toPlane = (m: { x: number; y: number }) => ({
    x: m.x * cross.x + m.y * cross.y,
    y: -(m.x * downhill.x + m.y * downhill.y) / cosPitch,
  });
  const angleFor = (azimuth: number) => {
    const vector = toPlane({
      x: Math.sin(degToRad(azimuth)),
      y: Math.cos(degToRad(azimuth)),
    });
    return Math.atan2(vector.y, vector.x);
  };
  const angle = angleFor(toBearing) - angleFor(fromBearing);
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return (point) => {
    if (fromBearing === toBearing) return point;
    const p = toPlane(frame.toM(point));
    const x = p.x * cos - p.y * sin;
    const y = p.x * sin + p.y * cos;
    return frame.toLatLng({
      x: x * cross.x - y * cosPitch * downhill.x,
      y: x * cross.y - y * cosPitch * downhill.y,
    });
  };
};

/** Rotate the outer ring and holes around the outer ring's centroid. */
export const rotateFacePolygon = (
  polygon: FacePackerPolygon,
  deltaDegrees: number
): FacePackerPolygon => {
  if (polygon.outer.length === 0 || deltaDegrees === 0) return polygon;
  const rotate = makePointRotation(ringCentroid(polygon.outer), deltaDegrees);
  return {
    outer: polygon.outer.map(rotate),
    holes: polygon.holes.map((hole) => hole.map(rotate)),
  };
};

/**
 * Compass bearing (degrees, folded into [0, 180) — edges are undirected) of
 * the longest edge of a ring. Null when the ring has fewer than 2 vertices.
 */
export const longestEdgeBearingDegrees = (
  ring: SolarLatLng[]
): number | null => {
  if (ring.length < 2) return null;
  const frame = makeFrame(ringCentroid(ring));
  const points = ring.map(frame.toM);
  let bestLengthSq = -Infinity;
  let bearing: number | null = null;
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const lengthSq = dx * dx + dy * dy;
    if (lengthSq > bestLengthSq && lengthSq > 0) {
      bestLengthSq = lengthSq;
      // atan2(east, north) = compass bearing of the edge direction.
      bearing = ((radToDeg(Math.atan2(dx, dy)) % 180) + 180) % 180;
    }
  }
  return bearing;
};

/**
 * The smallest rotation (clockwise degrees, within ±45) that snaps the face's
 * longest outer edge parallel to the nearest axis of its own azimuth frame
 * (down-slope or cross-slope — i.e. the nearest multiple of 90° relative to
 * `azimuthDegrees`). This is the "Straighten" button: one click squares a
 * skew-traced face up to its roof plane. Returns 0 when there's no edge.
 */
export const computeStraightenDeltaDegrees = (
  polygon: FacePackerPolygon,
  azimuthDegrees: number
): number => {
  const bearing = longestEdgeBearingDegrees(polygon.outer);
  if (bearing == null) return 0;
  // Rotating by +delta moves the edge bearing to (bearing + delta); we need
  // that ≡ azimuth (mod 90). Fold into (−45, 45] for the smallest rotation.
  let delta = (((azimuthDegrees - bearing) % 90) + 90) % 90;
  if (delta > 45) delta -= 90;
  return delta;
};
